import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["variables", "disabled"];
import { useEffect } from 'react';
import { useMutation } from 'data-fetching-client';
import { useHistory, generatePath } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { createCustomPropToSendOnTrigger, WootricCustomProp } from 'automation-ui-core/wootric/utils';
import { parseLocation } from 'automation-ui-routing/utils';
import { CREATE_FLOW } from 'automation-ui-canvas/queries/createFlowQuery';
import { PLATFORM_ACTION_EDIT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { FIRST_NODE_EDGE } from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { joinPathAndQuery } from 'automation-ui-routing/utils';
import { addAlert } from 'automation-ui-canvas/actions/MessageActions';
import * as AlertTypes from 'automation-ui-canvas/constants/AlertTypes';
import { CONTACT_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { isFilterEmpty } from 'automation-ui-flow-core/enrollment/enrollmentUtils';
import { getEnrollmentTrackingDataFromCombinedEnrollment, getEventTrackingDataWithTriggers } from 'automation-ui-enrollment/utils/trackingUtils';
import EnrollmentSchedule from 'automation-ui-enrollment/records/EnrollmentSchedule';
import FlowLimitsButton from 'automation-ui-canvas/create/FlowLimitsButton';
import { jsx as _jsx } from "react/jsx-runtime";
export default function CreateFlowButton(_ref) {
  var _parseLocation;
  let {
      variables,
      disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [createFlow, {
    data,
    loading,
    error
  }] = useMutation(CREATE_FLOW);
  const history = useHistory();
  const folderIdString = (_parseLocation = parseLocation(history.location)) === null || _parseLocation === void 0 ? void 0 : _parseLocation.folderId;
  const folderId = folderIdString ? Number(folderIdString) : variables.folderId;
  const dispatch = useDispatch();
  const handleCreateFlow = () => {
    // Track event in Wootric
    createCustomPropToSendOnTrigger(WootricCustomProp.CreateButtonClicked);
    return createFlow({
      variables: Object.assign({}, variables, {
        folderId
      })
    });
  };
  useEffect(() => {
    if (!data) {
      return;
    }
    const {
      createdFlow: {
        flowId,
        objectTypeId,
        name,
        enrollmentTrigger,
        enrollmentCriteria,
        enrollmentSchedule,
        shouldReenroll
      }
    } = data;
    const enrollmentType = enrollmentSchedule ? 'SCHEDULED' : enrollmentTrigger;
    let enrollmentTrackingData = {};
    if (enrollmentType === 'LIST' && (enrollmentCriteria === null || enrollmentCriteria === void 0 ? void 0 : enrollmentCriteria.triggerType) === 'LIST') {
      const filterBranch = (enrollmentCriteria === null || enrollmentCriteria === void 0 ? void 0 : enrollmentCriteria.triggerType) === 'LIST' ? enrollmentCriteria.filterBranch : undefined;
      enrollmentTrackingData = Object.assign({}, getEnrollmentTrackingDataFromCombinedEnrollment(filterBranch, []), {
        isEmpty: isFilterEmpty(filterBranch)
      });
    } else if (enrollmentType === 'EVENT') {
      enrollmentTrackingData = getEventTrackingDataWithTriggers((enrollmentCriteria === null || enrollmentCriteria === void 0 ? void 0 : enrollmentCriteria.triggerType) === 'EVENT' ? enrollmentCriteria : {});
    } else if (enrollmentType === 'SCHEDULED') {
      const filterBranch = (enrollmentCriteria === null || enrollmentCriteria === void 0 ? void 0 : enrollmentCriteria.triggerType) === 'LIST' ? enrollmentCriteria.filterBranch : undefined;
      enrollmentTrackingData = Object.assign({}, getEnrollmentTrackingDataFromCombinedEnrollment(filterBranch, []), {
        isEmpty: isFilterEmpty(filterBranch),
        frequency: new EnrollmentSchedule(enrollmentSchedule).getCadence()
      });
    }
    trackEvent(Trackables.CREATED_WORKFLOW, {
      flowId,
      name,
      // DRIP_DELAY is a relic of the classic workflows era we've preserved for data consistency
      type: objectTypeId === CONTACT_ID ? 'DRIP_DELAY' : objectTypeId,
      trigger: enrollmentType
    }, true);
    trackEvent(Trackables.SAVED_ENROLLMENT, Object.assign({
      flowId,
      shouldReenroll,
      objectTypeId,
      enrollmentType,
      editType: 'CREATE'
    }, enrollmentTrackingData), true);
    history.replace(joinPathAndQuery(generatePath(PLATFORM_ACTION_EDIT, {
      flowId,
      screen: 'edit',
      actionId: NEW_ACTION
    }), {
      edgeId: FIRST_NODE_EDGE
    }));
  }, [history, data]);
  useEffect(() => {
    if (error) {
      dispatch(addAlert(AlertTypes.CREATE_WORKFLOW_FAILED));
    }
  }, [dispatch, error]);
  return /*#__PURE__*/_jsx(FlowLimitsButton, Object.assign({
    use: "primary"
  }, rest, {
    // enables wrapping in UITooltip
    loading: loading,
    failed: !!error,
    disabled: disabled
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    ,
    onClick: handleCreateFlow,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "createWorkflow.createButton"
    })
  }));
}